import { supabase } from "@/integrations/supabase/client";
import type { Task } from "@/contexts/TaskContext";

/**
 * Premium task content is blocked at the database level: RLS on `tasks` only
 * returns premium rows to users who own the matching pack (plus admins and
 * moderators). To still show a locked, blurred teaser card we read a
 * content-free skeleton from the `get_premium_teaser_tasks` function.
 */

export interface PremiumTeaserRow {
  id: string;
  slug: string | null;
  category: string | null;
  categories: string[] | null;
  task_number: number | null;
  subpoint: number | null;
  task_type: string | null;
  task_subtype: string | null;
  use_yes_no: boolean | null;
  is_multi_select: boolean | null;
  is_two_points: boolean | null;
  max_points: number | null;
  answer_count: number | null;
  has_images: boolean | null;
  pack_id: string | null;
  created_at: string | null;
  is_unlocked: boolean | null;
}

export const LOCKED_QUESTION_PLACEHOLDER =
  "Treść tego zadania odblokujesz kupując pakiet premium.";

const LOCKED_ANSWER_PLACEHOLDER = "•••";

/** Builds a renderable, content-free Task used behind the premium paywall. */
export function mapTeaserRowToTask(row: PremiumTeaserRow): Task {
  const answerCount = row.answer_count ?? 0;

  return {
    id: row.id,
    slug: row.slug || undefined,
    question: LOCKED_QUESTION_PLACEHOLDER,
    instruction: undefined,
    sources: undefined,
    answers: Array.from({ length: answerCount }, () => LOCKED_ANSWER_PLACEHOLDER),
    correctIndex: -1,
    categories: row.categories || (row.category ? [row.category] : []),
    year: undefined,
    documentType: undefined,
    formula: undefined,
    taskNumber: row.task_number || undefined,
    subpoint: row.subpoint || undefined,
    imageUrls: undefined,
    imageThumbnails: undefined,
    imageCompressed: undefined,
    taskType: row.task_type || undefined,
    taskSubtype: row.task_subtype || undefined,
    useYesNo: row.use_yes_no || false,
    modelAnswer: undefined,
    prefilledAnswer: undefined,
    operationalVerb: undefined,
    tableData: undefined,
    matchingData: undefined,
    strikethroughText: undefined,
    isTwoPoints: row.is_two_points || false,
    maxPoints: row.max_points || (row.is_two_points ? 2 : 1),
    isMultiSelect: row.is_multi_select || false,
    tableScoringMode: null,
    customScoringScenarios: undefined,
    customScoringPoints: undefined,
    verdictOptions: undefined,
    multiTableData: undefined,
    isPremium: true,
  } as Task;
}

/** Fetches teaser skeletons for premium tasks (optionally limited to one pack). */
export async function fetchPremiumTeaserRows(
  packId?: string | null
): Promise<PremiumTeaserRow[]> {
  const { data, error } = await supabase.rpc("get_premium_teaser_tasks", {
    _pack_id: packId ?? null,
  });

  if (error) throw error;
  return (data || []) as unknown as PremiumTeaserRow[];
}
